/**
 * Read-only "Sign in as" (Isaiah Cotto, 2026-10-26; docs/security/ADMIN_SIGN_IN_AS.md).
 *
 * Where the tokens live, and why:
 *   - While viewing as somebody, localStorage `authToken` holds ONLY the 20-minute, read-only impersonation token.
 *     The administrator's own token is not kept anywhere in the page: the server revoked that session when the
 *     sign-in-as started, and `refreshToken` / `original_admin_token` are removed here. So nothing the viewed
 *     session's JavaScript can read would let it act as the administrator.
 *   - The way back is a one-time secret in an HttpOnly, SameSite=Strict cookie the server set on the start response,
 *     scoped to /api/v1/admin/impersonation. Page script cannot read it; exit sends it, and the server answers with a
 *     fresh administrator session.
 *   - The banner reads the claim from the token itself on every load (readImpersonation), so a reload never loses it.
 */
import { jwtDecode } from 'jwt-decode';
import config from '../config';

export const AUTH_TOKEN_CHANGED = 'emerge:auth-token-changed';
export const READ_ONLY_CODE = 'IMPERSONATION_READ_ONLY';

export interface ImpersonationClaim {
  impersonationId: string;
  targetName: string;
  targetEmail: string;
  adminName: string;
  /** epoch milliseconds */
  expiresAt: number;
}

interface RawClaims {
  impersonatedBy?: string;
  impersonationId?: string;
  readOnly?: boolean;
  targetName?: string;
  email?: string;
  impersonatedByName?: string;
  exp?: number;
}

function storage(): Storage | null {
  try { return window.localStorage; } catch { return null; }
}

/** The claim in a token, when it is one of ours (a read-only sign-in-as); null otherwise. */
export function claimOf(token: string | null | undefined): ImpersonationClaim | null {
  if (!token) return null;
  try {
    const c = jwtDecode<RawClaims>(token);
    if (!c || !c.impersonationId || c.readOnly !== true) return null;
    return {
      impersonationId: c.impersonationId,
      targetName: c.targetName || c.email || 'this person',
      targetEmail: c.email || '',
      adminName: c.impersonatedByName || '',
      expiresAt: (c.exp || 0) * 1000,
    };
  } catch {
    return null;
  }
}

/** The current page's sign-in-as, read from the stored token (never from React state). */
export function readImpersonation(): ImpersonationClaim | null {
  return claimOf(storage()?.getItem('authToken'));
}

/** Any impersonation token, including the sandbox kind (impersonatedBy only). */
export function isImpersonationToken(token: string | null | undefined): boolean {
  if (!token) return false;
  try {
    const c = jwtDecode<RawClaims>(token);
    return Boolean(c && (c.impersonatedBy || c.readOnly === true || c.impersonationId));
  } catch {
    return false;
  }
}

function announce() {
  try { window.dispatchEvent(new Event(AUTH_TOKEN_CHANGED)); } catch { /* no window */ }
}

function setSessionToken(token: string) {
  const s = storage();
  if (!s) return;
  s.setItem('authToken', token);
  try {
    const { exp } = jwtDecode<RawClaims>(token);
    if (exp) s.setItem('tokenExpiry', String(exp * 1000));
  } catch { /* leave expiry as is */ }
}

export interface SignInAsResult {
  ok: boolean;
  status?: number;
  code?: string;
  message?: string;
  target?: { id: string; name: string; email: string; role: string };
}

async function post(path: string, body: unknown, token: string | null): Promise<{ status: number; json: any }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers.Authorization = `Bearer ${token}`;
  const r = await fetch(`${config.apiBaseUrl}${path}`, {
    method: 'POST',
    headers,
    // The return cookie is set on the start response and sent on exit; same-origin on eMerge, `include` for a
    // cross-origin API in development.
    credentials: 'include',
    body: JSON.stringify(body ?? {}),
  });
  let json: any = null;
  try { json = await r.json(); } catch { json = null; }
  return { status: r.status, json };
}

/**
 * Start: the server checks SYSTEM_IMPERSONATE (SYSTEM_ADMIN or the Sign in as role), the administrator's own code and the reason. On success the page holds
 * only the read-only token from here on.
 */
export async function startSignInAs(userId: string, reason: string, totpCode: string): Promise<SignInAsResult> {
  const s = storage();
  const adminToken = s?.getItem('authToken') || null;
  let res;
  try {
    res = await post(`/admin/accounts/${encodeURIComponent(userId)}/impersonate`, { reason, totpCode }, adminToken);
  } catch {
    return { ok: false, message: 'The server could not be reached. Nothing changed.' };
  }
  if (res.status !== 200 || !res.json?.success || !res.json?.data?.token) {
    return { ok: false, status: res.status, code: res.json?.code, message: res.json?.message || 'Sign in as was refused.' };
  }
  if (s) {
    // The administrator's session is spent server-side; nothing of it stays in the page.
    s.removeItem('refreshToken');
    s.removeItem('original_admin_token');
    s.removeItem('impersonating_as');
  }
  setSessionToken(res.json.data.token);
  announce();
  return { ok: true, target: res.json.data.target };
}

let exiting: Promise<SignInAsResult> | null = null;

/**
 * Exit: the server ends the sign-in-as (revokes its session) and, with the return cookie, issues the administrator a
 * fresh session. On any failure the page keeps no token at all, so the next step is signing in again.
 */
export function exitSignInAs(): Promise<SignInAsResult> {
  if (exiting) return exiting;
  exiting = (async () => {
    const s = storage();
    const current = s?.getItem('authToken') || null;
    try {
      const res = await post('/admin/impersonation/exit', {}, current);
      if (res.status === 200 && res.json?.success && res.json?.data?.token) {
        setSessionToken(res.json.data.token);
        announce();
        return { ok: true };
      }
      s?.removeItem('authToken');
      s?.removeItem('tokenExpiry');
      announce();
      return { ok: false, status: res.status, code: res.json?.code, message: res.json?.message || 'Please sign in again.' };
    } catch {
      return { ok: false, message: 'The server could not be reached. Try Exit again.' };
    } finally {
      exiting = null;
    }
  })();
  return exiting;
}

/** The plain sentence for a refused write while viewing. */
export function readOnlyMessage(claim: ImpersonationClaim | null = readImpersonation()): string {
  return `Read-only while viewing as ${claim?.targetName || 'this person'}.`;
}
